import React, { useEffect, useState } from "react";
import { I } from "../data";
import { Loader2 } from "lucide-react";

/* Transparent step-by-step reveal used while a background job runs (chat answer,
   admit/aid compute, live university research). The step LABELS are passed in by
   the caller so they match the actual task — never a single generic list.
   Reuses the .compute-steps / .compute-step CSS. */
export function StepReveal({ steps = [], active, interval = 2200, testid = "step-reveal" }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!active || steps.length <= 1) { setStep(0); return; }
    setStep(0);
    const timers = [];
    // Advance through every step except the last; the final step keeps spinning
    // until the real work finishes and the caller unmounts this component.
    for (let i = 1; i < steps.length; i++) {
      timers.push(setTimeout(() => setStep(i), interval * i));
    }
    return () => timers.forEach(clearTimeout);
  }, [active, interval, steps.length]);

  if (!active || !steps.length) return null;
  return (
    <div className="compute-steps" data-testid={testid}>
      {steps.map((label, i) => {
        const state = i < step ? "done" : i === step ? "active" : "pending";
        return (
          <div key={i} className={`compute-step ${state}`} data-testid={`${testid}-step-${i}`}>
            <span className="cs-ico">
              {state === "done" ? <I.Check size={12} />
                : state === "active" ? <Loader2 size={12} className="vidhya-spin" />
                : <span className="cs-dot" />}
            </span>
            <span className="cs-label">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* Live university auto-research (uni add → background research). */
export const RESEARCH_STEPS = [
  "Finding the official program page",
  "Verifying tuition & fees",
  "Checking the application deadline",
  "Building your requirements checklist",
  "Scoring your fit",
];

/* Contextual steps for app-button-triggered inline tools (CopilotDrawer runAction). */
export function toolStepsForKind(kind) {
  switch (kind) {
    case "browse":
      return ["Finding the official page", "Reading admissions & fees", "Verifying the facts", "Summarizing for you"];
    case "fit":
      return ["Calculating your profile score", "Checking program requirements", "Cross-analyzing against admitted averages"];
    case "email":
      return ["Pulling the professor's recent work", "Connecting it to your background", "Drafting your email"];
    case "suggestUnis":
      return ["Understanding your profile", "Scanning matching programs", "Sorting reach / target / safety"];
    case "appPlan":
      return ["Reviewing your deadlines", "Sequencing the work", "Building your plan"];
    case "todo":
      return ["Reviewing your tasks", "Spotting what's urgent", "Building your shortlist"];
    case "advice":
    case "essay":
      return ["Reading your situation", "Thinking it through", "Putting it together"];
    default:
      return ["Working on it", "Almost there"];
  }
}

export function chatStepsForMessage(text) {
  const t = (text || "").toLowerCase();
  const has = (...words) => words.some((w) => t.includes(w));

  if (has("scholarship", "funding", "financial aid", "fund my", "afford", "grant"))
    return ["Reading your question", "Scanning funding sources", "Checking eligibility for your profile", "Writing your answer"];

  if (has("professor", "faculty", "supervisor", "lab", "phd advisor", "research group"))
    return ["Reading your question", "Locating the right department", "Matching faculty to your interests", "Writing your answer"];

  if (has("email", "draft", "outreach", "reach out", "write to"))
    return ["Reading your question", "Pulling the professor's recent work", "Drafting your email"];

  if (has(" vs ", "versus", "compare", "comparison", "better", "which one", "which of"))
    return ["Reading your question", "Pulling each program's data", "Comparing side by side", "Writing your answer"];

  if (has("deadline", "tuition", "fee", "requirement", "gpa", "ielts", "toefl", "gre", "acceptance", "tell me about", "research"))
    return ["Reading your question", "Searching official sources", "Verifying the facts", "Writing your answer"];

  if (has("task", "plan my", "what should i do", "this week", "next step", "to-do", "todo", "organize", "organise"))
    return ["Reading your question", "Reviewing your tasks & deadlines", "Building your plan"];

  if (has("chance", "odds", "admit", "get in", "fit", "shortlist", "suggest", "recommend"))
    return ["Understanding your profile", "Weighing your academic signals", "Reasoning through your options", "Writing your answer"];

  return ["Reading your question", "Thinking it through", "Writing your answer"];
}
